/**
 * worldChrome — reusable Phaser canvas helpers that paint the PLAZA's UI-kit look
 * (parchment panels, wood posts, gold/wood knobs, parchment speech bubbles) for
 * the in-stage canvas props, so the in-world chrome reads with the SAME visual
 * language as the plaza's DOM/CSS panels.
 *
 * The plaza chrome is DOM + CSS (`client/src/styles/ponpon.css` `.panel` / the
 * "Pon Pon Plaza" wood sign in `LobbyScene.ts`); the stage is Phaser canvas. The
 * shared SOURCE both consume is the token set — `client/src/config/theme.ts#COLORS`
 * mirrors `ponpon.css :root`. These helpers draw the canvas equivalent of the CSS
 * `.panel` / wood-sign recipe straight from those tokens, so a token tweak moves
 * the plaza CSS, the theme object, AND every in-stage prop together. Before this,
 * `StageScene` hand-drew signs / buttons / the door / bubbles with literal hex
 * (`0xf0808f`, `0xfff6e0`, …) that drifted from the plaza palette.
 *
 * **Client-visual only** — pure presentation; no wire/Schema/authority surface.
 */

import Phaser from 'phaser';
import { COLORS } from '../config/theme.js';

/** Hex string token (e.g. `#a9763f`) → Phaser 0xRRGGBB int. */
function hex(token: string): number {
  return Phaser.Display.Color.HexStringToColor(token).color;
}

/** The plaza's panel/wood/parchment palette, resolved once from the theme tokens. */
const WOOD = hex(COLORS.wood); // --wood   #a9763f
const WOOD_D = hex(COLORS.woodD); // --wood-d #7c5024
const WOOD_L = hex(COLORS.woodL); // --wood-l #c79055
const PANEL = hex(COLORS.panel); // --panel  #f5e6c2 (parchment fill)
const PANEL_HI = hex(COLORS.panelHi); // --panel-hi #fdf3d8 (inset highlight)
const SOIL_D = hex(COLORS.soilD); // --soil-d #6b4424 (deep wood stroke / drop)
const GOLD = hex(COLORS.gold); // --gold   #f3b33a
const GOLD_D = hex(COLORS.goldD); // --gold-d #c98a22
const LEAF = hex(COLORS.leaf); // --leaf   #6ab04c (pressed knob)
const LEAF_D = hex(COLORS.leafD); // --leaf-d #4d8a33
const PINK = hex(COLORS.pink); // --pink   #ec6a86 (idle knob)
const PINK_D = hex(COLORS.pinkD); // --pink-d #c4475f

export { WOOD, WOOD_D, WOOD_L, PANEL, PANEL_HI, SOIL_D };

/**
 * A wooden post (the plaza sign's vertical pole, a wood gradient column with a
 * deep-wood edge). `cx` = post centre x, `topY`..`baseY` its span (screen px,
 * y down). Drawn into the caller's `g`.
 */
export function drawWoodPost(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  topY: number,
  baseY: number,
  width = 7,
): void {
  const half = width / 2;
  // body (wood) + a darker right edge so it reads round, like the plaza pole's
  // `linear-gradient(90deg,#5e3a1f,#7c4a26,#5e3a1f)`.
  g.fillStyle(WOOD_D, 1);
  g.fillRect(cx - half, topY, width, baseY - topY);
  g.fillStyle(WOOD_L, 1);
  g.fillRect(cx - half + 1, topY, Math.max(1, width - 3), baseY - topY);
}

/**
 * A parchment board (the plaza `.panel` look — parchment fill, wood border, inset
 * highlight, soft drop) centred at (cx, cy) with size (w, h) and corner radius.
 * Use for sign boards / framed props so they read as the plaza's panel language.
 */
export function drawParchmentBoard(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  w: number,
  h: number,
  radius = 10,
): void {
  const x = cx - w / 2;
  const y = cy - h / 2;
  // soft drop shadow (the CSS `box-shadow: 0 12px 26px -10px …`).
  g.fillStyle(SOIL_D, 0.22);
  g.fillRoundedRect(x + 1, y + 4, w, h, radius);
  // parchment fill + wood border + inset highlight.
  g.fillStyle(PANEL, 1);
  g.fillRoundedRect(x, y, w, h, radius);
  g.lineStyle(3, WOOD, 1);
  g.strokeRoundedRect(x, y, w, h, radius);
  g.lineStyle(2, PANEL_HI, 0.9);
  g.strokeRoundedRect(x + 2.5, y + 2.5, w - 5, h - 5, Math.max(2, radius - 3));
}

/**
 * The plaza's wood "Pon Pon Plaza" sign board — a warm wood gradient board with
 * a deep-wood border + drop (NOT parchment), matching `LobbyScene.ts`'s sign.
 * Centred at (cx, cy). Used for the stage sign boards so they match the plaza's
 * worded wood signs.
 */
export function drawWoodSign(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  w: number,
  h: number,
  radius = 10,
): void {
  const x = cx - w / 2;
  const y = cy - h / 2;
  // drop (the plaza's `box-shadow: 0 4px 0 #5e3a1f`).
  g.fillStyle(SOIL_D, 1);
  g.fillRoundedRect(x, y + 4, w, h, radius);
  // wood gradient body (top lighter → bottom darker, like `#c79055→#a0703c`).
  g.fillStyle(WOOD_L, 1);
  g.fillRoundedRect(x, y, w, h, radius);
  g.fillStyle(WOOD, 0.5);
  g.fillRoundedRect(x, y + h / 2, w, h / 2, { tl: 0, tr: 0, bl: radius, br: radius });
  g.lineStyle(3, SOIL_D, 1);
  g.strokeRoundedRect(x, y, w, h, radius);
}

/**
 * A button knob in the plaza's button-kit language — a raised rounded cap on a
 * wood base plate, idle pink / pressed green (matching the `.btn.pink` / leaf
 * tokens), with a deep-wood plate like the `.btn` `box-shadow`. `cx`,`baseY` =
 * the base-plate centre-bottom on the ground; redraws the whole knob into `g`.
 */
export function drawButtonKnob(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  baseY: number,
  pressed: boolean,
): void {
  // wood base plate.
  g.fillStyle(WOOD_D, 1);
  g.fillRoundedRect(cx - 22, baseY - 12, 44, 12, 5);
  g.fillStyle(WOOD, 1);
  g.fillRoundedRect(cx - 22, baseY - 13, 44, 6, 5);
  // the raised cap — sinks + greens when pressed (latched), pink when idle.
  const capH = pressed ? 8 : 16;
  const capColor = pressed ? LEAF : PINK;
  const capEdge = pressed ? LEAF_D : PINK_D;
  g.fillStyle(capColor, 1);
  g.fillRoundedRect(cx - 16, baseY - 12 - capH, 32, capH, 6);
  g.fillStyle(PANEL_HI, 0.35);
  g.fillRoundedRect(cx - 16, baseY - 12 - capH, 32, Math.max(3, capH * 0.4), 6);
  g.lineStyle(3, capEdge, 1);
  g.strokeRoundedRect(cx - 16, baseY - 12 - capH, 32, capH, 6);
}

/**
 * A parchment speech bubble (the plaza panel look) sized to (w, h), top-left at
 * (x, y), with an optional little downward tail centred under it. Matches the
 * plaza chat/panel parchment + wood border. Drawn into the caller's `g` (cleared
 * by the caller). `tail` adds the pointer that aims the bubble at a head below.
 */
export function drawParchmentBubble(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  radius = 10,
  tail = false,
): void {
  // soft drop.
  g.fillStyle(SOIL_D, 0.18);
  g.fillRoundedRect(x + 1, y + 3, w, h, radius);
  // parchment fill + wood border + inset highlight (the `.panel` recipe).
  g.fillStyle(PANEL, 1);
  g.fillRoundedRect(x, y, w, h, radius);
  g.lineStyle(2.5, WOOD, 1);
  g.strokeRoundedRect(x, y, w, h, radius);
  g.lineStyle(1.5, PANEL_HI, 0.9);
  g.strokeRoundedRect(x + 2, y + 2, w - 4, h - 4, Math.max(2, radius - 2));
  if (tail) {
    const cx = x + w / 2;
    const baseY = y + h;
    g.fillStyle(PANEL, 1);
    g.fillTriangle(cx - 7, baseY - 1, cx + 7, baseY - 1, cx, baseY + 9);
    g.lineStyle(2.5, WOOD, 1);
    g.lineBetween(cx - 7, baseY - 1, cx, baseY + 9);
    g.lineBetween(cx + 7, baseY - 1, cx, baseY + 9);
  }
}

/**
 * A goal door in the plaza's wood-frame language — a warm wood arch with a
 * deep-wood border + gold knob, so the stage goal reads like a plaza wood frame.
 * Drawn into `frame` (a graphics local to the door container, origin = door
 * centre-bottom). `w`,`h` = door size. Returns nothing.
 */
export function drawWoodDoor(
  frame: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
): void {
  const corners = { tl: 30, tr: 30, bl: 6, br: 6 };
  // wood body + a darker lower band so it reads as a grained wood frame.
  frame.fillStyle(WOOD_L, 1);
  frame.fillRoundedRect(-w / 2, -h, w, h, corners);
  frame.fillStyle(WOOD, 0.45);
  frame.fillRoundedRect(-w / 2, -h * 0.5, w, h * 0.5, { tl: 0, tr: 0, bl: 6, br: 6 });
  // inset parchment panel (the door's "open to the next room" warmth).
  frame.fillStyle(PANEL, 0.35);
  frame.fillRoundedRect(-w / 2 + 7, -h + 9, w - 14, h - 16, { tl: 22, tr: 22, bl: 4, br: 4 });
  frame.lineStyle(4, SOIL_D, 1);
  frame.strokeRoundedRect(-w / 2, -h, w, h, corners);
}

/** The plaza gold token for a door knob (matches `--gold`). */
export const KNOB_GOLD = GOLD;
export const KNOB_GOLD_D = GOLD_D;
